<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Activity, CheckCircle2, CloudUpload, Database, FlaskConical, RefreshCw, ShieldCheck, XCircle } from "@lucide/vue";
import {
  createUploadSignature,
  fetchConfig,
  fetchDatabases,
  pingDatabases,
  uploadToOss,
  type DatabaseInfo,
  type DatabasePingResult,
  type RuntimeConfig
} from "../services/api";

const config = ref<RuntimeConfig | null>(null);
const databases = ref<DatabaseInfo[]>([]);
const pingResults = ref<DatabasePingResult[]>([]);
const loading = ref(false);
const pinging = ref(false);
const uploadBusy = ref(false);
const uploadUrl = ref("");
const errorMessage = ref("");

const readonlyCount = computed(() => databases.value.filter((db) => db.readonly).length);
const dbStatusMap = computed(() => new Map(pingResults.value.map((result) => [result.key, result])));

onMounted(() => {
  void refresh();
});

async function refresh(): Promise<void> {
  loading.value = true;
  errorMessage.value = "";
  try {
    const [runtimeConfig, dbs] = await Promise.all([
      fetchConfig(),
      fetchDatabases()
    ]);
    config.value = runtimeConfig;
    databases.value = dbs;
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "加载失败";
  } finally {
    loading.value = false;
  }
}

async function checkConnections(): Promise<void> {
  pinging.value = true;
  errorMessage.value = "";
  try {
    pingResults.value = await pingDatabases();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "检查失败";
  } finally {
    pinging.value = false;
  }
}

async function handleFileChange(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (!file) {
    return;
  }

  uploadBusy.value = true;
  uploadUrl.value = "";
  errorMessage.value = "";
  try {
    const signature = await createUploadSignature(file);
    uploadUrl.value = await uploadToOss(file, signature);
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : "上传失败";
  } finally {
    uploadBusy.value = false;
    input.value = "";
  }
}
</script>

<template>
  <main class="shell">
    <section class="topbar">
      <div>
        <p class="eyebrow">Unified Framework</p>
        <h1>{{ config?.appName ?? "统一开发框架" }}</h1>
      </div>
      <button class="icon-button" type="button" :disabled="loading" title="刷新" @click="refresh">
        <RefreshCw :size="18" />
      </button>
      <RouterLink class="text-button test-link" to="/workbench-test">
        <FlaskConical :size="16" />
        工作台接口测试
      </RouterLink>
    </section>

    <p v-if="errorMessage" class="alert">{{ errorMessage }}</p>

    <section class="metrics">
      <article class="metric">
        <Database :size="22" />
        <span>数据库连接</span>
        <strong>{{ databases.length }}</strong>
      </article>
      <article class="metric">
        <ShieldCheck :size="22" />
        <span>只读业务库</span>
        <strong>{{ readonlyCount }}</strong>
      </article>
      <article class="metric">
        <CloudUpload :size="22" />
        <span>OSS Bucket</span>
        <strong>{{ config?.oss.bucket ?? "-" }}</strong>
      </article>
    </section>

    <section class="layout">
      <div class="panel">
        <div class="panel-title">
          <h2>数据库</h2>
          <button class="text-button" type="button" :disabled="pinging" @click="checkConnections">
            <Activity :size="16" />
            <span>{{ pinging ? "检查中" : "连接检查" }}</span>
          </button>
        </div>

        <div class="table">
          <div class="table-row table-head">
            <span>名称</span>
            <span>库名</span>
            <span>类型</span>
            <span>状态</span>
          </div>
          <div v-for="db in databases" :key="db.key" class="table-row">
            <span>{{ db.name }}</span>
            <span>{{ db.database }}</span>
            <span>{{ db.readonly ? "只读" : "应用库" }}</span>
            <span class="status" :class="{ ok: dbStatusMap.get(db.key)?.ok, bad: dbStatusMap.get(db.key)?.ok === false }">
              <CheckCircle2 v-if="dbStatusMap.get(db.key)?.ok" :size="16" />
              <XCircle v-else-if="dbStatusMap.get(db.key)?.ok === false" :size="16" />
              {{ dbStatusMap.get(db.key)?.message ?? "未检查" }}
            </span>
          </div>
        </div>
      </div>

      <div class="panel">
        <div class="panel-title">
          <h2>OSS 上传</h2>
          <span class="muted">{{ config?.oss.region ?? "-" }}</span>
        </div>

        <label class="upload-box">
          <CloudUpload :size="28" />
          <span>{{ uploadBusy ? "上传中" : "选择文件" }}</span>
          <input type="file" :disabled="uploadBusy" @change="handleFileChange" />
        </label>

        <a v-if="uploadUrl" class="result-link" :href="uploadUrl" target="_blank" rel="noreferrer">
          {{ uploadUrl }}
        </a>
      </div>
    </section>
  </main>
</template>
